import {
	CheckCircle,
	RefreshCw,
	Layers,
	Shield,
	Users,
	Link,
	AlertTriangle,
	Scale,
	UserX,
	FileText,
	Phone,
	Building2,
	Mail,
	MapPin,
	ExternalLink,
	ChevronRight,
} from "lucide-react";

const SectionHeader = ({ num, icon: Icon, title }) => (
	<div className="flex items-center gap-4 mb-8">
		<div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center text-white">
			<Icon size={22} />
		</div>
		<div>
			<div className="text-purple-400 font-mono text-sm">
				SECTION {String(num).padStart(2, "0")}
			</div>
			<h2 className="text-3xl font-bold text-white">{title}</h2>
		</div>
	</div>
);

export default function TermsAndConditions() {
	return (
		<div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden">
			{/* Subtle grid background */}
			<div className="fixed inset-0 opacity-10">
				<div
					className="absolute inset-0"
					style={{
						backgroundImage: `
              linear-gradient(to right, rgba(148, 163, 184, 0.15) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(148, 163, 184, 0.15) 1px, transparent 1px)
            `,
						backgroundSize: "50px 50px",
					}}
				/>
			</div>

			<div className="container px-6 sm:px-12 py-20 relative z-10">
				{/* Header */}
				{/* <header className="mb-24">
					<div className="flex items-center gap-4 text-slate-400 font-mono md:text-lg flex-wrap">
						<span>Last updated: [Date]</span>
					</div>
					<div className="mt-8 h-px bg-gradient-to-r from-purple-600 via-blue-600 to-transparent" />
				</header> */}

				{/* 1. Acceptance of Terms */}
				<section className="mb-20">
					<SectionHeader num={1} icon={CheckCircle} title="Acceptance of Terms" />
					<div className="space-y-6 text-slate-300 leading-relaxed">
						<p className="text-lg">
							These Terms of Use ("Terms") govern your access to and use of the SAN micron website located at{" "}
							<span className="text-purple-400">sanmicron.com</span> (the "Website"), including all sub-domains, pages, and services offered through it. The Website is operated by SAN micron ("we", "our", "us"), a company based in Australia.
						</p>
						<p>
							By accessing or using our Website, you agree to be bound by these Terms. If you do not agree, please do not use our Website.
						</p>
					</div>
				</section>

				{/* 2. Changes to Terms */}
				<section className="mb-20">
					<SectionHeader num={2} icon={RefreshCw} title="Changes to Terms" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							We reserve the right to modify or replace these Terms at any time at our sole discretion. The most current version will be posted on this page with the "Last updated" date. By continuing to access or use our Website after revisions become effective, you agree to be bound by the revised Terms.
						</p>
					</div>
				</section>

				{/* 3. Description of Services */}
				<section className="mb-20">
					<SectionHeader num={3} icon={Layers} title="Description of Services" />
					<p className="text-slate-300 leading-relaxed mb-6">
						SAN micron provides a wide range of IT solutions and services, including but not limited to:
					</p>
					<div className="grid md:grid-cols-2 gap-6 mb-6">
						{[
							{
								title: "Core IT Services",
								items: ["IT Services Packages", "IT Consulting", "Infrastructure Management"],
							},
							{
								title: "Development & Technology",
								items: ["Software Development", "AI Solutions", "Cloud Solutions", "Cyber Security"],
							},
							{
								title: "Digital & Marketing",
								items: ["SEO & Digital Marketing", "UI/UX Design", "Web Hosting & Domain Registration", "E-commerce Solutions"],
							},
							{
								title: "Communication & Industries",
								items: [
									"3CX VOIP System",
									"Education, Insurance, Retail",
									"Healthcare, Travel & Logistics",
									"Banking, Manufacturing, Engineering & more",
								],
							},
						].map((card, index) => (
							<div
								key={index}
								className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300"
							>
								<h3 className="text-lg font-semibold text-white mb-4">{card.title}</h3>
								<ul className="space-y-3 text-slate-300 text-sm">
									{card.items.map((item, i) => (
										<li key={i} className="flex items-start gap-2">
											<ChevronRight size={14} className="text-purple-400 mt-0.5 flex-shrink-0" />
											<span>{item}</span>
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
					<p className="text-slate-400">
						We may modify, suspend, or discontinue any part of our services without notice.
					</p>
				</section>

				{/* 4. Intellectual Property */}
				<section className="mb-20">
					<SectionHeader num={4} icon={Shield} title="Intellectual Property Rights" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p className="mb-4">
							Unless otherwise stated, all content on this Website — including text, graphics, logos, icons, images, audio clips, software, and the compilation thereof (collectively, "Content") — is the property of SAN micron or its content suppliers and is protected by Australian and international copyright laws. The SAN micron name and logo are trademarks of SAN micron.
						</p>
						<p>
							You may not reproduce, distribute, modify, create derivative works of, publicly display, or commercially exploit any Content without our prior written permission.
						</p>
					</div>
				</section>

				{/* 5. User Conduct */}
				<section className="mb-20">
					<SectionHeader num={5} icon={Users} title="User Conduct" />
					<p className="text-slate-300 leading-relaxed mb-6">
						You agree to use the Website only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the Website. Prohibited behaviours include:
					</p>
					<div className="space-y-4">
						{[
							"Harassing or causing distress or inconvenience to any person.",
							"Transmitting obscene or offensive content.",
							"Disrupting the normal flow of dialogue within our Website.",
							"Providing inaccurate or incomplete information when using our support or contact forms.",
						].map((text, index) => (
							<div
								key={index}
								className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-5 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 flex items-center gap-4"
							>
								<div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center font-mono text-white md:text-lg flex-shrink-0">
									{String(index + 1).padStart(2, "0")}
								</div>
								<p className="text-slate-300 leading-relaxed">{text}</p>
							</div>
						))}
					</div>
				</section>

				{/* 6. Third-Party Links */}
				<section className="mb-20">
					<SectionHeader num={6} icon={Link} title="Third-Party Links and Affiliations" />
					<p className="text-slate-300 leading-relaxed mb-6">
						Our Website may contain links to third-party websites, plug-ins, and applications, including those of our affiliates and partners. We do not control these third-party websites and are not responsible for their privacy statements or terms.
					</p>
					<div className="grid sm:grid-cols-3 gap-4">
						{[
							"Google", "Microsoft", "Digital Ocean", "3CX", "Crazy Domain", "Synology", "Ezmed LLC", "Amazon Cloud (AWS)", "Azure", "N-able",
						].map((partner, index) => (
							<div
								key={index}
								className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 px-4 py-6 hover:border-purple-500/50 transition-all duration-300 text-center"
							>
								<span className="text-slate-200 font-medium">{partner}</span>
							</div>
						))}
					</div>
					<p className="text-slate-400 mt-6">
						We encourage you to read the terms and privacy policies of any third-party site you visit.
					</p>
				</section>

				{/* 7. Disclaimer of Warranties */}
				<section className="mb-20">
					<SectionHeader num={7} icon={AlertTriangle} title="Disclaimer of Warranties" />
					<div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-8 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300">
						<p className="text-slate-300 leading-relaxed mb-4">
							YOUR USE OF THE WEBSITE IS AT YOUR SOLE RISK. THE WEBSITE AND ALL CONTENT, SERVICES, AND PRODUCTS AVAILABLE THROUGH IT ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
						</p>
						<p className="text-slate-300 leading-relaxed">
							WE DO NOT WARRANT THAT THE WEBSITE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE WEBSITE OR THE SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
						</p>
					</div>
				</section>

				{/* 8. Limitation of Liability */}
				<section className="mb-20">
					<SectionHeader num={8} icon={Scale} title="Limitation of Liability" />
					<div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-8 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300">
						<p className="text-slate-300 leading-relaxed mb-4">
							TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SAN MICRON, ITS DIRECTORS, EMPLOYEES, PARTNERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
						</p>
						<div className="space-y-3 mt-4">
							{[
								"Your access to or use of or inability to access or use the Website.",
								"Any conduct or content of any third party on the Website.",
								"Any content obtained from the Website.",
								"Unauthorized access, use, or alteration of your transmissions or content, Whether under warranty, contract, tort (including negligence), or any other legal theory, even if we have been informed that such damages might occur.",
							].map((item, index) => (
								<div key={index} className="flex items-start gap-3">
									<div className="w-9 h-9 rounded bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
										<span className="text-white font-mono md:text-lg">{index + 1}</span>
									</div>
									<p className="text-slate-400 uppercase">{item}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* 9. Indemnification */}
				<section className="mb-20">
					<SectionHeader num={9} icon={Shield} title="Indemnification" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p className="mb-4">
							You agree to defend, indemnify, and hold harmless SAN micron and its employees, contractors, and agents from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including but not limited to attorney's fees) arising from:
						</p>
						<div className="space-y-3 mt-4">
							{[
								"Your use of and access to the Website.",
								"Your violation of any term of these Terms.",
								"Your violation of any third-party right, including any copyright, property, or privacy right.",
								"Any claim that your use of the Website caused damage to a third party.",
							].map((item, index) => (
								<div key={index} className="flex items-center gap-3">
									<div className="w-9 h-9 rounded bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
										<span className="text-white font-mono md:text-lg">{index + 1}</span>
									</div>
									<p className="text-slate-400">{item}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* 10. Governing Law */}
				<section className="mb-20">
					<SectionHeader num={10} icon={Scale} title="Governing Law and Dispute Resolution" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							These Terms shall be governed by and construed in accordance with the laws of New South Wales, Australia, without regard to its conflict of law provisions. Any dispute arising out of or relating to these Terms or your use of the Website shall be resolved exclusively in the courts of New South Wales, Australia. You consent to the personal jurisdiction of such courts.
						</p>
					</div>
				</section>

				{/* 11. Termination */}
				<section className="mb-20">
					<SectionHeader num={11} icon={UserX} title="Termination" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							We may terminate or suspend your access to all or part of the Website, without notice, for any conduct that we, in our sole discretion, believe violates these Terms or is harmful to other users of the Website, us, or third parties, or for any other reason.
						</p>
					</div>
				</section>

				{/* 12. Entire Agreement */}
				<section className="mb-20">
					<SectionHeader num={12} icon={FileText} title="Entire Agreement" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							These Terms constitute the entire agreement between you and SAN micron regarding your use of the Website and supersede any prior agreements.
						</p>
					</div>
				</section>

				{/* 13. Contact */}
				<section className="mb-20">
					<SectionHeader num={13} icon={Phone} title="Contact Information" />
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						{[
							{ Icon: Building2, label: "Company", value: "SAN micron", href: null },
							{ Icon: Phone, label: "Phone", value: "+61 391 167 215", href: "tel:+61391167215" },
							{ Icon: Mail, label: "Email", value: "info@sanmicron.com", href: "mailto:info@sanmicron.com" },
							{ Icon: MapPin, label: "Address", value: "Melbourne, Australia", href: null },
							{ Icon: ExternalLink, label: "Contact Form", value: "sanmicron.com/contact/", href: "https://sanmicron.com/contact/" },
						].map(({ Icon, label, value, href }, index) => (
							<div
								key={index}
								className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300"
							>
								<div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0 text-white">
									<Icon size={20} />
								</div>
								<div>
									<div className="text-gray-300 mb-1">{label}</div>
									{href ? (
										<a
											href={href}
											target={href.startsWith("http") ? "_blank" : undefined}
											rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
											className="text-gray-200 hover:text-purple-400 transition-colors duration-200 underline-offset-2 hover:underline"
										>
											{value}
										</a>
									) : (
										<div className="text-gray-200">{value}</div>
									)}
								</div>
							</div>
						))}
					</div>
				</section>

			</div>
		</div>
	);
}